/**
 * @module useTealiumTrigger
 * @description Custom hooks for triggering Tealium analytics events
 */

import { useState, useEffect } from 'react';
import { getEventConfig, collectDatabridgeDatapoints } from '../tools/tealium';

/**
 * @function useTealiumTrigger
 * @description Hook that provides a function to trigger predefined Tealium events
 *
 * @param {string} featureType - The feature category (e.g., 'universal', 'checkout')
 * @param {string} interaction - The specific user interaction/event key
 * @returns {Function} triggerEvent - Function that triggers the Tealium event when called
 *
 * @example
 * const trackAddToCart = useTealiumTrigger('product', 'ADD_TO_CART');
 *
 * // Later in your component:
 * <button onClick={() => {
 *   addToCart(product);
 *   trackAddToCart(); // Triggers the Tealium event
 * }}>
 *   Add to Cart
 * </button>
 */
export default function useTealiumTrigger(featureType, interaction) {
  const [digitalData, setDigitalData] = useState();

  /**
   * @function triggerEvent
   * @description Triggers the Tealium event with predefined configuration
   */
  const triggerEvent = () => {
    if (!digitalData) return;

    const eventConfig = getEventConfig(featureType);

    digitalData.trigger(eventConfig[interaction].eventName, eventConfig[interaction].eventData);
  };

  useEffect(() => {
    if (!window?.digitalData) return;

    setDigitalData(window.digitalData);
  }, []);

  return triggerEvent;
}

/**
 * @function useCustomTealiumTrigger
 * @description Hook that provides a function to trigger custom Tealium events with provided data
 *
 * @param {Object} tealiumData - Custom event configuration
 * @param {string} tealiumData.eventName - Name of the Tealium event to trigger
 * @param {Object} tealiumData.eventData - Data to send with the event
 * @param {string} [tealiumData.eventData.event_Id] - Event identifier
 * @returns {Function} triggerEvent - Function that triggers the custom Tealium event when called
 *
 * @example
 * const trackCustomEvent = useCustomTealiumTrigger({
 *   eventName: 'custom_interaction',
 *   eventData: {
 *     event_Id: 'profile_update',
 *     user_status: 'registered'
 *   }
 * });
 *
 * // Later in your component:
 * <button onClick={() => {
 *   updateProfile();
 *   trackCustomEvent();
 * }}>
 *   Update Profile
 * </button>
 */
export const useCustomTealiumTrigger = (tealiumData, includeDatabridgeDatapoints = true) => {
  const [digitalData, setDigitalData] = useState();

  /**
   * @function triggerEvent
   * @description Triggers the custom Tealium event with provided configuration
   */
  const triggerEvent = () => {
    if (!digitalData) return;

    const dataBridgeDataPoints = (tealiumData.eventData?.event_Id !== 'marketing_brand' && includeDatabridgeDatapoints) ? collectDatabridgeDatapoints(digitalData) : '';
    const globalDatapoints = {
      ...dataBridgeDataPoints,
      ...tealiumData.eventData,
    };

    digitalData.trigger(tealiumData.eventName, globalDatapoints);
  };

  useEffect(() => {
    if (!window?.digitalData) return;

    setDigitalData(window.digitalData);
  }, []);

  return triggerEvent;
};
